import { create } from 'zustand';
import { api } from '@/lib/api-client';
import {
  type TransactionDto,
  type HistoryFilters,
  type WebappRawTransaction,
  WebappTransactionsResponseSchema,
  DEFAULT_HISTORY_FILTERS,
} from './types';

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

interface HistoryState {
  transactions: TransactionDto[];
  isLoading: boolean;
  error: string | null;
  total: number;
  filters: HistoryFilters;
  page: number;
  limit: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';

  fetchTransactions: (clientId: string) => Promise<void>;
  setFilters: (filters: Partial<HistoryFilters>) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSortBy: (field: string, direction: 'asc' | 'desc') => void;
  clearError: () => void;
}

// ---------------------------------------------------------------------------
// Build query params from state
// ---------------------------------------------------------------------------

function buildParams(
  filters: HistoryFilters,
  page: number,
  limit: number,
  sortBy: string,
  sortDirection: string,
): Record<string, string> {
  const params: Record<string, string> = {
    page: String(page),
    limit: String(limit),
  };

  if (filters.type && filters.type !== 'all') params.type = filters.type;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.searchQuery) params.searchQuery = filters.searchQuery;
  if (sortBy) {
    params.sortBy = sortBy;
    params.sortDirection = sortDirection;
  }

  return params;
}

function toNumber(value: number | string | undefined | null): number | undefined {
  if (value === undefined || value === null) return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function toStringOrEmpty(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : String(value);
}

function deriveId(raw: WebappRawTransaction): number | undefined {
  if (raw.type === 'payment') return toNumber(raw.payment_id);
  if (raw.type === 'fx_trade') return toNumber(raw.fx_trade_id);
  return toNumber(raw.incoming_payment_id);
}

function deriveCurrency(raw: WebappRawTransaction): string {
  if (raw.currency) return raw.currency;
  if (raw.currency_code) return raw.currency_code;
  if (raw.type === 'fx_trade' && raw.sell_currency) return raw.sell_currency;
  return '';
}

const PAYMENT_STATUS_LABELS: Record<number, string> = {
  1: 'pending',
  2: 'processing',
  3: 'completed',
  4: 'rejected',
  5: 'cancelled',
};

function deriveStatus(raw: WebappRawTransaction): string {
  if (raw.status) return raw.status;
  const id =
    raw.type === 'payment'
      ? toNumber(raw.payment_status)
      : raw.type === 'fx_trade'
        ? toNumber(raw.fx_trade_status)
        : toNumber(raw.incoming_payment_status);
  if (id !== undefined && PAYMENT_STATUS_LABELS[id]) return PAYMENT_STATUS_LABELS[id];
  return '';
}

function mapRawToTransactionDto(raw: WebappRawTransaction): TransactionDto | null {
  const id = deriveId(raw);
  const clientId = toNumber(raw.client_id ?? raw.client);
  if (clientId === undefined) return null;

  return {
    transaction_id: raw.transaction_id ?? '',
    id,
    client_id: clientId,
    transaction_type: raw.type,
    status: deriveStatus(raw),
    amount: toStringOrEmpty(raw.amount),
    currency: deriveCurrency(raw),
    reference_currency: raw.buy_currency ?? undefined,
    rate: raw.rate !== null && raw.rate !== undefined ? String(raw.rate) : undefined,
    fee_applied:
      raw.fee_applied !== null && raw.fee_applied !== undefined
        ? String(raw.fee_applied)
        : undefined,
    sender_name: raw.sender_name ?? undefined,
    beneficiary_name: raw.beneficiary_name ?? undefined,
    created_at: raw.created_at,
    updated_at: raw.updated_at ?? raw.created_at,
    description: raw.description ?? undefined,
  };
}

export const useHistory = create<HistoryState>((set, get) => ({
  transactions: [],
  isLoading: false,
  error: null,
  total: 0,
  filters: { ...DEFAULT_HISTORY_FILTERS },
  page: 1,
  limit: 50,
  sortBy: 'created_at',
  sortDirection: 'desc',

  fetchTransactions: async (clientId: string) => {
    const { filters, page, limit, sortBy, sortDirection } = get();
    set({ isLoading: true, error: null });

    try {
      const params = buildParams(filters, page, limit, sortBy, sortDirection);
      const data = await api.get<unknown>(`/api/clients/${clientId}/transactions`, { params });

      const parsed = WebappTransactionsResponseSchema.safeParse(data);
      if (!parsed.success) {
        set({ isLoading: false, error: 'Invalid response format from server.' });
        return;
      }

      const transactions = parsed.data.data
        .map(mapRawToTransactionDto)
        .filter(Boolean) as TransactionDto[];

      set({
        transactions,
        total: parsed.data.total,
        isLoading: false,
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to load transactions.';
      set({ isLoading: false, error: message });
    }
  },

  setFilters: (partial: Partial<HistoryFilters>) => {
    const current = get().filters;
    set({ filters: { ...current, ...partial }, page: 1 });
  },

  clearFilters: () => {
    set({ filters: { ...DEFAULT_HISTORY_FILTERS }, page: 1 });
  },

  setPage: (page: number) => {
    set({ page });
  },

  setLimit: (limit: number) => {
    set({ limit, page: 1 });
  },

  setSortBy: (field: string, direction: 'asc' | 'desc') => {
    set({ sortBy: field, sortDirection: direction });
  },

  clearError: () => {
    set({ error: null });
  },
}));
